import React, { useState } from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  padding: 20px;
`;

const SearchSection = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  align-items: center;
`;

const SearchBar = styled.div`
  flex: 1;
  
  input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 16px;
    
    &:focus {
      outline: none;
      border-color: #007bff;
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
    }
  }
`;

const AddContactButton = styled.button`
  padding: 12px 24px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #0056b3;
  }
`;

const TabContainer = styled.div`
  margin-bottom: 20px;
`;

const TabList = styled.div`
  display: flex;
  gap: 10px;
  border-bottom: 1px solid #ddd;
  margin-bottom: 20px;
`;

const Tab = styled.button`
  padding: 12px 24px;
  border: none;
  background: none;
  font-size: 16px;
  font-weight: 500;
  color: ${props => props.active ? '#007bff' : '#666'};
  border-bottom: 2px solid ${props => props.active ? '#007bff' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #007bff;
  }
`;

const ContactList = styled.div`
  display: grid;
  gap: 15px;
`;

const ContactCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
`;

const ContactInfo = styled.div`
  h3 {
    margin: 0 0 5px;
    font-size: 16px;
    color: #333;
  }

  .contact-details {
    margin: 0 0 10px;
    color: #666;
    font-size: 14px;
    display: flex;
    gap: 15px;

    a {
      color: #007bff;
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 4px;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .notes {
    margin: 10px 0 0;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 4px;
    font-size: 14px;
    color: #666;
    border: 1px solid #dee2e6;
    min-height: 60px;
  }

  textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    min-height: 60px;
    resize: vertical;

    &:focus {
      outline: none;
      border-color: #007bff;
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
    }
  }
`;

const ContactActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &.edit {
    background: #e9ecef;
    color: #495057;
    
    &:hover {
      background: #dee2e6;
    }
  }

  &.delete {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
`;

const AddButton = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 56px;
  height: 56px;
  border-radius: 28px;
  background: #007bff;
  color: white;
  border: none;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  transition: all 0.2s ease;

  &:hover {
    background: #0056b3;
    transform: translateY(-2px);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);

  h2 {
    margin: 0 0 20px;
    font-size: 20px;
    color: #333;
  }
`;

const NotesModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const NotesContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);

  h2 {
    margin: 0 0 20px;
    font-size: 20px;
    color: #333;
  }

  textarea {
    width: 100%;
    min-height: 150px;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    margin-bottom: 20px;
    font-size: 14px;
    resize: vertical;

    &:focus {
      outline: none;
      border-color: #007bff;
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
    }
  }
`;

const Form = styled.form`
  display: grid;
  gap: 15px;

  input {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #007bff;
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;

  button {
    padding: 10px 20px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    border: none;

    &.cancel {
      background: #e9ecef;
      color: #495057;
    }

    &.save {
      background: #007bff;
      color: white;
    }
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
`;

const CRMPage = () => {
  const [activeTab, setActiveTab] = useState('owners');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [contacts, setContacts] = useState({
    owners: [
      { id: 1, name: 'John Doe', email: 'john@example.com', phone: '(555) 123-4567', notes: 'Owner of 123 Main St' },
      { id: 2, name: 'Jane Smith', email: 'jane@example.com', phone: '(555) 234-5678', notes: 'Looking to sell in 6 months' },
    ],
    buyers: [
      { id: 1, name: 'Mike Johnson', email: 'mike@example.com', phone: '(555) 345-6789', notes: 'Pre-approved for $500k' },
      { id: 2, name: 'Sarah Williams', email: 'sarah@example.com', phone: '(555) 456-7890', notes: 'Looking in downtown area' },
    ],
    residents: [
      { id: 1, name: 'Tom Brown', email: 'tom@example.com', phone: '(555) 567-8901', notes: 'Lease ends in August' },
      { id: 2, name: 'Lisa Davis', email: 'lisa@example.com', phone: '(555) 678-9012', notes: 'Month-to-month tenant' },
    ],
    agents: [
      { id: 1, name: 'David Miller', email: 'david@realty.com', phone: '(555) 789-0123', notes: 'Coldwell Banker' },
      { id: 2, name: 'Emily Chen', email: 'emily@realty.com', phone: '(555) 890-1234', notes: 'ReMax agent' },
    ],
  });

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredContacts = contacts[activeTab].filter(contact => {
    const query = searchQuery.toLowerCase();
    return (
      contact.name.toLowerCase().includes(query) ||
      contact.email.toLowerCase().includes(query) ||
      contact.phone.includes(query)
    );
  });

  const handleAdd = () => {
    setEditingContact(null);
    setShowModal(true);
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setShowModal(true);
  };

  const handleDelete = (contactId) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      setContacts({
        ...contacts,
        [activeTab]: contacts[activeTab].filter(c => c.id !== contactId)
      });
    }
  };

  const handleEditNotes = (contact) => {
    setEditingNotes(contact);
  };

  const handleNotesChange = (e, contactId) => {
    setContacts({
      ...contacts,
      [activeTab]: contacts[activeTab].map(c =>
        c.id === contactId ? { ...c, notes: e.target.value } : c
      )
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newContact = {
      id: editingContact ? editingContact.id : Date.now(),
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
    };

    if (editingContact) {
      setContacts({
        ...contacts,
        [activeTab]: contacts[activeTab].map(c => 
          c.id === editingContact.id ? newContact : c
        )
      });
    } else {
      setContacts({
        ...contacts,
        [activeTab]: [...contacts[activeTab], newContact]
      });
    }

    setShowModal(false);
  };

  return (
    <PageContainer>
      <SearchSection>
        <SearchBar>
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={handleSearch}
          />
        </SearchBar>
        <AddContactButton onClick={handleAdd}>
          ➕ Add Contact
        </AddContactButton>
      </SearchSection>

      <TabContainer>
        <TabList>
          <Tab 
            active={activeTab === 'owners'} 
            onClick={() => setActiveTab('owners')}
          >
            Owners
          </Tab>
          <Tab 
            active={activeTab === 'buyers'} 
            onClick={() => setActiveTab('buyers')}
          >
            Buyers
          </Tab>
          <Tab 
            active={activeTab === 'residents'} 
            onClick={() => setActiveTab('residents')}
          >
            Residents
          </Tab>
          <Tab 
            active={activeTab === 'agents'} 
            onClick={() => setActiveTab('agents')}
          >
            Agents
          </Tab>
        </TabList>

        <ContactList>
          {filteredContacts.length > 0 ? (
            filteredContacts.map(contact => (
              <ContactCard key={contact.id}>
                <ContactInfo>
                  <h3>{contact.name}</h3>
                  <div className="contact-details">
                    <a href={`mailto:${contact.email}`} title="Send email">
                      ✉️ {contact.email}
                    </a>
                    <a href={`tel:${contact.phone}`} title="Call">
                      📞 {contact.phone}
                    </a>
                    <a href={`sms:${contact.phone}`} title="Send SMS">
                      💬 Text
                    </a>
                  </div>
                  {contact.notes ? (
                    <div className="notes" onClick={() => handleEditNotes(contact)}>
                      {contact.notes}
                    </div>
                  ) : (
                    <div className="notes" onClick={() => handleEditNotes(contact)}>
                      Click to add notes...
                    </div>
                  )}
                </ContactInfo>
                <ContactActions>
                  <ActionButton 
                    className="edit"
                    onClick={() => handleEdit(contact)}
                  >
                    Edit
                  </ActionButton>
                  <ActionButton 
                    className="delete"
                    onClick={() => handleDelete(contact.id)}
                  >
                    Delete
                  </ActionButton>
                </ContactActions>
              </ContactCard>
            ))
          ) : (
            <NoResults>
              No contacts found. Try adjusting your search or add a new contact.
            </NoResults>
          )}
        </ContactList>
      </TabContainer>

      <AddButton onClick={handleAdd}>+</AddButton>

      {/* Notes Modal */}
      {editingNotes && (
        <NotesModal>
          <NotesContent>
            <h2>Edit Notes for {editingNotes.name}</h2>
            <textarea
              value={editingNotes.notes || ''}
              onChange={(e) => handleNotesChange(e, editingNotes.id)}
              placeholder="Add notes about this contact..."
            />
            <ButtonRow>
              <button 
                type="button" 
                className="cancel"
                onClick={() => setEditingNotes(null)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="save"
                onClick={() => setEditingNotes(null)}
              >
                Save Notes
              </button>
            </ButtonRow>
          </NotesContent>
        </NotesModal>
      )}

      {/* Add/Edit Contact Modal */}
      {showModal && (
        <Modal>
          <ModalContent>
            <h2>{editingContact ? 'Edit Contact' : 'Add New Contact'}</h2>
            <Form onSubmit={handleSubmit}>
              <input
                type="text"
                name="name"
                placeholder="Name"
                required
                defaultValue={editingContact?.name}
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                required
                defaultValue={editingContact?.email}
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone"
                required
                defaultValue={editingContact?.phone}
              />
              <ButtonRow>
                <button 
                  type="button" 
                  className="cancel"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="save">
                  {editingContact ? 'Save Changes' : 'Add Contact'}
                </button>
              </ButtonRow>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

export default CRMPage;